import { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as DigitalTwinIcon } from "../../../../assets/images/doll_front.svg";
import { useRemoteRenderContext } from "../../../../classes/contexts/RemoteRenderContext";
import {
  InstanceOrientation,
  MODALITY,
  MORPHOLOGY_ORIENTATION_ORDER,
  SPINESIM_MODALITIES,
} from "../../../../classes/enums/MedicalImagesEnum";
import { convertKey } from "../../../../classes/utils/MedicalImagesUtils";
import ClinicalHistoryThumbnail from "../../../../components/clinicalHistoryThumbnail";
import ClinicalHistoryViewer from "../../../../components/clinicalHistoryViewer";
import DataPlaceholder from "../../../../components/dataPlaceholder/DataPlaceholder";
import ImageCarousel from "../../../../components/imageCarousel/ImageCarousel";
import { MedicalImageThumbnail } from "../../../../components/medicalImageThumbnail/MedicalImageThumbnail";
import PainAssessmentThumbnail from "../../../../components/painAssessmentThumbnail/PainAssessmentThumbnail";
import { PosturalImageThumbnail } from "../../../../components/posturalImageThumbnail/PosturalImageThumbnail";
import RemoteViewer from "../../../../components/remoteViewer/RemoteViewer";
import ViewLoader from "../../../../components/viewLoader/ViewLoader";
import { CORE_CONFIG } from "../../../../config/digitalTwinCoreConfig";
import { REFINE_CONFIG } from "../../../../config/digitalTwinRefineConfig";
import useFileStorage from "../../../../hooks/useFileStorage";
import PainAssessmentViewer from "../../../common/painAssesment/PainAssessmentViewer";
import { getRemoteMedicalViewState } from "../../../../classes/utils/RemoteMedicalViewStateUtils";
import useTranslation from "../../../../hooks/i18n/useTranslation";

import "./digitalTwinCreationWidget.css";

const normalizeOrientation = orientation => {
  if (!orientation) return "";

  const normalized = orientation.toLowerCase();

  if (normalized.includes(InstanceOrientation.Coronal?.toLowerCase())) {
    return InstanceOrientation.Coronal;
  }

  if (normalized.includes(InstanceOrientation.Sagittal?.toLowerCase())) {
    return InstanceOrientation.Sagittal;
  }

  if (normalized.includes("axial")) {
    return "axial";
  }

  return normalized;
};

const getSelectedItem = (selectedItems, config) => {
  const { modality, orientation } = config;

  if (modality === MODALITY.DX && orientation) {
    const dxItems = selectedItems[modality] || [];
    const targetNormalizedOrientation = normalizeOrientation(orientation);

    return dxItems.find(item => {
      const itemNormalizedOrientation = normalizeOrientation(item.orientation);
      return itemNormalizedOrientation === targetNormalizedOrientation;
    });
  }

  if (modality === SPINESIM_MODALITIES.POSTURE && orientation) {
    return selectedItems[modality]?.find(
      item => item.orientation === orientation
    );
  }

  if (modality === SPINESIM_MODALITIES.PAIN) {
    return selectedItems[modality]?.[0];
  }

  if (modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
    return selectedItems[modality]?.[0];
  }

  return selectedItems[modality]?.[0];
};

const generateCacheKey = item => {
  if (!item) return null;
  return `${item.tenant_id}_${item.patient_id}_${item._id}_${item.file_name}`;
};

const generateConfigKey = config => {
  if (!config) return null;
  return `${config.modality}_${config.orientation || ""}_${config.key || ""}`;
};

const getAllPossibleMedicalViews = (creationStep, viewCache, selectedItems) => {
  const allViews = [];

  const allConfigs = [...CORE_CONFIG];
  if (creationStep === 1) {
    allConfigs.push(
      ...REFINE_CONFIG.filter(
        config => config.modality !== SPINESIM_MODALITIES.POSTURE
      )
    );
  }

  allConfigs.forEach(config => {
    const selectedItem = getSelectedItem(selectedItems, config);
    if (selectedItem) {
      const cacheKey = generateCacheKey(selectedItem);
      if (viewCache.hasView(cacheKey)) {
        const cachedData = viewCache.getView(cacheKey);

        if (Array.isArray(cachedData)) {
          cachedData.forEach((view, index) => {
            allViews.push({
              key: `${cacheKey}_${view.viewName || index}`,
              viewData: view,
              instance: selectedItem,
              cacheKey: cacheKey,
            });
          });
        } else {
          allViews.push({
            key: cacheKey,
            viewData: cachedData,
            instance: selectedItem,
            cacheKey: cacheKey,
          });
        }
      }
    }
  });

  return allViews;
};

const DigitalTwinCreationWidget = ({
  creationStep,
  selectedItems,
  renderClient,
}) => {
  const [activeViewType, setActiveViewType] = useState("icon");
  const [activePostureIndex, setActivePostureIndex] = useState(null);
  const [lastSelectedItems, setLastSelectedItems] = useState(selectedItems);
  const [activeMedicalViewKey, setActiveMedicalViewKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingItem, setLoadingItem] = useState(null);
  const prevRenderClientRef = useRef(renderClient);

  const { getMedicalImagePath } = useFileStorage();
  const {
    digitalTwinCreationCache,
    isConnected: isRemoteRenderConnected,
    isLoading: isRemoteRenderLoading,
  } = useRemoteRenderContext();
  const { t } = useTranslation();
  const getOrderedPostureImages = () => {
    if (!selectedItems || !selectedItems[SPINESIM_MODALITIES.POSTURE])
      return { images: [], orientationToIndex: {} };

    const postureItems = selectedItems[SPINESIM_MODALITIES.POSTURE] || [];

    const orderedImages = [];
    const orientationToIndex = {};
    let currentIndex = 0;

    MORPHOLOGY_ORIENTATION_ORDER.forEach(orientation => {
      const matchingItems = postureItems.filter(
        item => item.orientation === orientation
      );

      matchingItems.forEach(item => {
        if (item.url) {
          orientationToIndex[orientation] = currentIndex;
          orderedImages.push(item.url);
          currentIndex++;
        }
      });
    });

    const nonStandardItems = postureItems.filter(
      item => !MORPHOLOGY_ORIENTATION_ORDER.includes(item.orientation)
    );

    nonStandardItems.forEach(item => {
      const imageUrl = item.url;
      if (imageUrl) {
        orientationToIndex[item.orientation] = currentIndex;
        orderedImages.push(imageUrl);
        currentIndex++;
      }
    });

    return { images: orderedImages, orientationToIndex };
  };

  const { images: orderedPostureImages, orientationToIndex } =
    getOrderedPostureImages();

  useEffect(() => {}, [orderedPostureImages]);

  const handlePostureClick = useCallback(
    orientation => {
      const actualCarouselIndex = orientationToIndex[orientation];

      if (actualCarouselIndex === undefined) {
        return;
      }

      if (
        activeViewType === "posture" &&
        activePostureIndex === actualCarouselIndex
      ) {
        setActiveViewType("icon");
        setActivePostureIndex(null);
      } else {
        setActiveViewType("posture");
        setActivePostureIndex(actualCarouselIndex);
      }
      setActiveMedicalViewKey(null);
    },
    [activeViewType, activePostureIndex, orientationToIndex]
  );

  const handlePainAssessmentClick = useCallback(
    config => {
      const selectedItem = getSelectedItem(selectedItems, config);
      if (!selectedItem) return;

      if (activeViewType === "pain") {
        setActiveViewType("icon");
      } else {
        setActiveViewType("pain");
      }
      setActivePostureIndex(null);
      setActiveMedicalViewKey(null);
    },
    [activeViewType, selectedItems]
  );

  const handleClinicalHistoryClick = useCallback(
    config => {
      const selectedItem = getSelectedItem(selectedItems, config);
      if (!selectedItem) return;

      if (activeViewType === "clinicalHistory") {
        setActiveViewType("icon");
      } else {
        setActiveViewType("clinicalHistory");
      }
      setActivePostureIndex(null);
      setActiveMedicalViewKey(null);
    },
    [activeViewType, selectedItems]
  );

  const handleMedicalImageClick = useCallback(
    async config => {
      try {
        if (!renderClient) {
          return;
        }

        const selectedItem = getSelectedItem(selectedItems, config);

        if (!selectedItem) {
          return;
        }

        const cacheKey = generateCacheKey(selectedItem);

        if (activeViewType === "medical" && activeMedicalViewKey === cacheKey) {
          setActiveViewType("icon");
          setActiveMedicalViewKey(null);
          return;
        }
        if (!digitalTwinCreationCache.hasView(cacheKey)) {
          const path = await getMedicalImagePath(
            selectedItem.tenant_id,
            selectedItem.patient_id,
            selectedItem._id,
            selectedItem.file_name
          );

          const viewType = convertKey(
            selectedItem.modality,
            selectedItem.orientation
          );

          const viewConfig = {
            path: path,
            view: viewType,
          };

          const response = await renderClient.loadSpinesimVisualizationView(
            JSON.stringify(viewConfig)
          );

          let viewData;
          if (typeof response === "string") {
            try {
              viewData = JSON.parse(response);
            } catch (e) {
              viewData = { viewId: "error", viewName: "error" };
            }
          } else {
            viewData = response;
          }

          if (Array.isArray(viewData)) {
            viewData.forEach(view => {
              view.cacheKey = cacheKey;
              view.instance = selectedItem;
            });
          } else {
            viewData.cacheKey = cacheKey;
            viewData.instance = selectedItem;
          }

          digitalTwinCreationCache.setView(cacheKey, viewData);
        }

        setActiveViewType("medical");
        setActiveMedicalViewKey(cacheKey);
        setActivePostureIndex(null);
      } catch (error) {
        throw new Error("Error in handleMedicalImageClick: " + error.message);
      }
    },
    [
      renderClient,
      selectedItems,
      activeViewType,
      activeMedicalViewKey,
      digitalTwinCreationCache,
      getMedicalImagePath,
    ]
  );

  const handleThumbnailClick = useCallback(
    async config => {
      if (isLoading) {
        return;
      }

      if (config.modality === SPINESIM_MODALITIES.POSTURE) {
        handlePostureClick(config.orientation);
      } else if (config.modality === SPINESIM_MODALITIES.PAIN) {
        handlePainAssessmentClick(config);
        return;
      } else if (config.modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
        handleClinicalHistoryClick(config);
        return;
      } else {
        setLoadingItem(generateConfigKey(config));
        setIsLoading(true);

        try {
          await handleMedicalImageClick(config);
        } finally {
          setIsLoading(false);
          setLoadingItem(null);
        }
      }
    },
    [
      isLoading,
      handlePostureClick,
      handlePainAssessmentClick,
      handleClinicalHistoryClick,
      handleMedicalImageClick,
    ]
  );

  const handleCarouselImageChange = newIndex => {
    setActivePostureIndex(newIndex);
  };

  useEffect(() => {
    if (selectedItems !== lastSelectedItems) {
      const getPreviousPostureMapping = () => {
        if (!lastSelectedItems?.[SPINESIM_MODALITIES.POSTURE]) return {};

        const previousPostureItems =
          lastSelectedItems[SPINESIM_MODALITIES.POSTURE] || [];
        const previousMapping = {};
        let currentIndex = 0;

        MORPHOLOGY_ORIENTATION_ORDER.forEach(orientation => {
          const matchingItems = previousPostureItems.filter(
            item => item.orientation === orientation
          );
          matchingItems.forEach(item => {
            if (item.url) {
              previousMapping[orientation] = currentIndex;
              currentIndex++;
            }
          });
        });

        return previousMapping;
      };

      const resetToIcon = () => {
        setActiveViewType("icon");
        setActiveMedicalViewKey(null);
        setActivePostureIndex(null);
      };

      const setPostureView = index => {
        setActiveViewType("posture");
        setActivePostureIndex(index);
        setActiveMedicalViewKey(null);
      };

      const hasItemChanged = (currentItem, previousItem) => {
        if (!currentItem) return false;
        if (!previousItem) return true;

        // For medical images (have _id), identity is the ID
        if (currentItem._id || previousItem._id) {
          return currentItem._id !== previousItem._id;
        }
        // For posture items (no _id), identity is the title (stable filename).
        // Blob URL changes alone (from fetchPatientImages refresh) do not indicate
        // a different image was selected, so we intentionally ignore url changes here.
        return currentItem.title !== previousItem.title;
      };

      const handleMedicalDeselection = () => {
        if (activeViewType !== "medical" || !activeMedicalViewKey) return false;

        const activeItemStillExists = CORE_CONFIG.some(config => {
          const currentItem = getSelectedItem(selectedItems, config);
          return (
            currentItem &&
            generateCacheKey(currentItem) === activeMedicalViewKey
          );
        });

        if (!activeItemStillExists) {
          resetToIcon();
          return true;
        }
        return false;
      };

      const handlePostureDeselection = () => {
        if (activeViewType !== "posture" || activePostureIndex === null)
          return false;

        const previousOrientationToIndex = getPreviousPostureMapping();
        const previouslyActiveOrientation = Object.keys(
          previousOrientationToIndex
        ).find(
          orientation =>
            previousOrientationToIndex[orientation] === activePostureIndex
        );

        if (previouslyActiveOrientation) {
          const stillExists = selectedItems[SPINESIM_MODALITIES.POSTURE]?.find(
            item => item.orientation === previouslyActiveOrientation
          );

          if (!stillExists) {
            resetToIcon();
            return true;
          } else {
            const newIndex = orientationToIndex[previouslyActiveOrientation];
            if (newIndex !== undefined && newIndex !== activePostureIndex) {
              setActivePostureIndex(newIndex);
            }
          }
        } else {
          resetToIcon();
          return true;
        }
        return false;
      };

      const handlePainDeselection = () => {
        if (activeViewType !== "pain") return false;

        const painItem = selectedItems[SPINESIM_MODALITIES.PAIN]?.[0];
        if (!painItem) {
          setActiveViewType("icon");
          return true;
        }
        return false;
      };

      const handleClinicalHistoryDeselection = () => {
        if (activeViewType !== "clinicalHistory") return false;

        const clinicalHistoryItem =
          selectedItems[SPINESIM_MODALITIES.CLINICAL_HISTORY]?.[0];
        if (!clinicalHistoryItem) {
          setActiveViewType("icon");
          return true;
        }
        return false;
      };

      const handleNewNonPostureSelections = () => {
        const changedViewerSlots = CORE_CONFIG.concat(REFINE_CONFIG).filter(
          config => {
            const currentItem = getSelectedItem(selectedItems, config);
            const previousItem = getSelectedItem(lastSelectedItems, config);

            return (
              currentItem &&
              (!previousItem || currentItem._id !== previousItem._id) &&
              config.modality !== SPINESIM_MODALITIES.POSTURE
            );
          }
        );

        if (changedViewerSlots.length > 0) {
          const newConfig = changedViewerSlots[0];
          // Pain and clinical history use a toggle click handler — calling it
          // when the view is already active would dismiss it instead of showing
          // the newly selected item. Set the view type directly to avoid this.
          if (newConfig.modality === SPINESIM_MODALITIES.PAIN) {
            setActiveViewType("pain");
            setActivePostureIndex(null);
            setActiveMedicalViewKey(null);
          } else if (
            newConfig.modality === SPINESIM_MODALITIES.CLINICAL_HISTORY
          ) {
            setActiveViewType("clinicalHistory");
            setActivePostureIndex(null);
            setActiveMedicalViewKey(null);
          } else {
            handleThumbnailClick(newConfig);
          }
          return true;
        }
        return false;
      };

      const handlePostureChanges = () => {
        const changedPostureConfigs = REFINE_CONFIG.filter(config => {
          if (config.modality !== SPINESIM_MODALITIES.POSTURE) return false;

          const currentItem = getSelectedItem(selectedItems, config);
          const previousItem = getSelectedItem(lastSelectedItems, config);

          return hasItemChanged(currentItem, previousItem);
        });

        for (const changedPostureConfig of changedPostureConfigs) {
          const previousItem = getSelectedItem(
            lastSelectedItems,
            changedPostureConfig
          );
          const orientation = changedPostureConfig.orientation;
          const newIndex = orientationToIndex[orientation];

          if (!previousItem) {
            if (newIndex !== undefined) {
              setPostureView(newIndex);
            }
            break;
          } else {
            if (newIndex !== undefined) {
              setPostureView(newIndex);
            }
            break;
          }
        }
      };

      // Main execution flow
      const wasDeselected =
        handleMedicalDeselection() ||
        handlePostureDeselection() ||
        handlePainDeselection() ||
        handleClinicalHistoryDeselection();

      if (!wasDeselected) {
        const hadNewSelection = handleNewNonPostureSelections();
        if (!hadNewSelection) {
          handlePostureChanges();
        }
      }

      setLastSelectedItems(selectedItems);
    }
  }, [
    lastSelectedItems,
    selectedItems,
    activeViewType,
    activeMedicalViewKey,
    activePostureIndex,
    orientationToIndex,
    handleThumbnailClick,
  ]);

  // Auto-show the first selected medical image when the render client first connects.
  // The main selectedItems effect exits early when renderClient is null, so we need
  // this separate effect to retry once the client becomes available.
  useEffect(() => {
    const clientJustConnected = !!renderClient && !prevRenderClientRef.current;
    prevRenderClientRef.current = renderClient ?? null;

    if (!clientJustConnected || activeViewType !== "icon") return;

    const firstMedicalConfig = CORE_CONFIG.find(config =>
      !!getSelectedItem(selectedItems, config)
    );

    if (firstMedicalConfig) {
      handleThumbnailClick(firstMedicalConfig);
    }
  }, [renderClient, activeViewType, selectedItems, handleThumbnailClick]);

  // Pass the first selected medical modality so getRemoteMedicalViewState can
  // recognise it as a remote-render modality and produce a correct isConnecting state.
  const firstSelectedMedicalModality = CORE_CONFIG.reduce((found, config) => {
    if (found) return found;
    return getSelectedItem(selectedItems, config) ? config.modality : null;
  }, null);

  const remoteMedicalViewState = getRemoteMedicalViewState({
    modality: firstSelectedMedicalModality,
    views: [],
    isLocalLoading: isLoading,
    isRemoteRenderConnected,
    isRemoteRenderLoading,
  });
  const remoteMedicalLoadingMessage = remoteMedicalViewState.isConnecting
    ? t("474")
    : t("475");

  // Only show the connecting/loading overlay when the active view requires the
  // render client. Posture, pain, and clinical-history views do not need it.
  const needsRenderClient =
    activeViewType === "icon" || activeViewType === "medical";
  const viewLoaderIsLoading =
    isLoading || (remoteMedicalViewState.isConnecting && needsRenderClient);

  const renderMainContent = () => {
    switch (activeViewType) {
      case "icon":
        return (
          <div className="content-container">
            <DigitalTwinIcon className="DigitalTwinCreationWidget-icon" />
          </div>
        );

      case "pain":
        // eslint-disable-next-line no-case-declarations
        const painAssessment = getSelectedItem(selectedItems, {
          modality: SPINESIM_MODALITIES.PAIN,
        });
        return (
          <div className="content-container">
            <div
              className="pain-assessment-container"
              style={{ width: "100%", flex: 1, marginBottom: "20px" }}
            >
              <PainAssessmentViewer assessment={painAssessment} />
            </div>
          </div>
        );

      case "clinicalHistory":
        // eslint-disable-next-line no-case-declarations
        const clinicalHistory = getSelectedItem(selectedItems, {
          modality: SPINESIM_MODALITIES.CLINICAL_HISTORY,
        });
        return (
          <div className="content-container">
            <div
              className="clinical-history-container"
              style={{ width: "100%", flex: 1, marginBottom: "20px" }}
            >
              <ClinicalHistoryViewer history={clinicalHistory} />
            </div>
          </div>
        );

      case "medical":
        return (
          <div className="content-container">
            <div
              className="RemoteViewer-container"
              style={{ width: "100%", flex: 1, marginBottom: "20px" }}
            >
              {getAllPossibleMedicalViews(
                creationStep,
                digitalTwinCreationCache,
                selectedItems
              ).map(view => {
                const isActive =
                  view.cacheKey === activeMedicalViewKey &&
                  (!Array.isArray(
                    digitalTwinCreationCache.getView(activeMedicalViewKey)
                  ) ||
                    view.viewData ===
                      digitalTwinCreationCache.getView(
                        activeMedicalViewKey
                      )[0]);

                return (
                  <div
                    key={view.key}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: isActive ? "block" : "none",
                    }}
                  >
                    <RemoteViewer
                      remoteRenderClient={renderClient}
                      viewData={view.viewData}
                      sliderWidth="80%"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "posture":
      default:
        return (
          <div className="content-container">
            <div className="carousel-container">
              <ImageCarousel
                images={orderedPostureImages}
                activeIndex={activePostureIndex}
                onImageClick={handleCarouselImageChange}
              />
            </div>
          </div>
        );
    }
  };

  const renderContent = () => {
    return (
      <>
        <div className="DigitalTwinCreationWidget-core-elements">
          {CORE_CONFIG.map((config, index) => {
            const selectedItem = getSelectedItem(selectedItems, config);
            const configKey = generateConfigKey(config);
            const isCurrentlyLoading = loadingItem === configKey;

            if (selectedItem) {
              return (
                <div
                  key={index}
                  className={`selected-thumbnail-wrapper ${
                    isLoading ? "disabled" : ""
                  }`}
                >
                  <MedicalImageThumbnail
                    instance={selectedItem}
                    isSelected={
                      activeMedicalViewKey === generateCacheKey(selectedItem) ||
                      isCurrentlyLoading
                    }
                    onClick={() => handleThumbnailClick(config)}
                  />
                </div>
              );
            } else {
              return (
                creationStep === 0 && (
                  <DataPlaceholder
                    key={index}
                    title={config.modality}
                    comment={config.orientation}
                  />
                )
              );
            }
          })}
        </div>

        <ViewLoader
          isLoading={viewLoaderIsLoading}
          className="DigitalTwinCreationWidget-wrapper"
          message={remoteMedicalLoadingMessage}
        >
          {!viewLoaderIsLoading && renderMainContent()}
        </ViewLoader>

        <div className="DigitalTwinCreationWidget-refine-elements">
          {creationStep === 1 &&
            REFINE_CONFIG.map((config, index) => {
              const selectedItem = getSelectedItem(selectedItems, config);
              const configKey = generateConfigKey(config);
              const isCurrentlyLoading = loadingItem === configKey;

              if (selectedItem) {
                const isActive =
                  config.modality === SPINESIM_MODALITIES.POSTURE
                    ? activePostureIndex ===
                        orientationToIndex[config.orientation] &&
                      activeViewType === "posture"
                    : config.modality === SPINESIM_MODALITIES.PAIN
                      ? activeViewType === "pain"
                      : config.modality === SPINESIM_MODALITIES.CLINICAL_HISTORY
                        ? activeViewType === "clinicalHistory"
                        : activeMedicalViewKey ===
                          generateCacheKey(selectedItem);

                if (config.modality === SPINESIM_MODALITIES.PAIN) {
                  return (
                    <div
                      key={index}
                      className={`selected-thumbnail-wrapper ${
                        isLoading ? "disabled" : ""
                      }`}
                    >
                      <PainAssessmentThumbnail
                        assessment={selectedItem}
                        isSelected={isActive || isCurrentlyLoading}
                        onClick={() => handleThumbnailClick(config)}
                      />
                    </div>
                  );
                }

                // For clinical history
                if (config.modality === SPINESIM_MODALITIES.CLINICAL_HISTORY) {
                  return (
                    <div
                      key={index}
                      className={`selected-thumbnail-wrapper ${
                        isLoading ? "disabled" : ""
                      }`}
                    >
                      <ClinicalHistoryThumbnail
                        history={selectedItem}
                        isSelected={isActive || isCurrentlyLoading}
                        onClick={() => handleThumbnailClick(config)}
                      />
                    </div>
                  );
                }

                // For posture images
                if (config.modality === SPINESIM_MODALITIES.POSTURE) {
                  return (
                    <div
                      key={index}
                      className={`selected-thumbnail-wrapper ${
                        isLoading ? "disabled" : ""
                      }`}
                    >
                      <PosturalImageThumbnail
                        image={selectedItem}
                        isSelected={isActive || isCurrentlyLoading}
                        onClick={() => handleThumbnailClick(config)}
                      />
                    </div>
                  );
                }

                // For medical images (DX modality)
                return (
                  <div
                    key={index}
                    className={`selected-thumbnail-wrapper ${
                      isLoading ? "disabled" : ""
                    }`}
                  >
                    <MedicalImageThumbnail
                      instance={selectedItem}
                      isSelected={isActive || isCurrentlyLoading}
                      onClick={() => handleThumbnailClick(config)}
                    />
                  </div>
                );
              } else {
                return (
                  <DataPlaceholder
                    key={index}
                    title={config.modality}
                    comment={config.orientation}
                  />
                );
              }
            })}
        </div>
      </>
    );
  };
  return (
    <div className="DigitalTwinCreationWidget-container">
      <div className="DigitalTwinCreationWidget-content">{renderContent()}</div>
    </div>
  );
};

export default DigitalTwinCreationWidget;
